import { Injectable } from '@angular/core';

import * as LaunchDarklyClient from 'launchdarkly-js-client-sdk';
import {
  BehaviorSubject, Observable, from, of
} from 'rxjs';
import {
  map, tap, catchError, first
} from 'rxjs/operators';

import { v4 as uuidv4 } from 'uuid';

import { ConfigurationService, ILaunchDarklyAppConfig } from '@sondermind/configuration';
import {
  ILaunchDarklyService, LDBooleanFeatureFlags, LDStringFeatureFlags
} from '@sondermind/launch-darkly';
import { ResponseUpdateItem } from '@sondermind/utilities/models-flows';

import { UserType } from '../models/launch-darkly-user.enum';

@Injectable()
export class FlowsLaunchDarklyService implements ILaunchDarklyService {
  uuid: string = '';
  userType = UserType.CLIENT;
  client: LaunchDarklyClient.LDClient | undefined;
  clientLoaded: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  ldFlagSubject: BehaviorSubject<LaunchDarklyClient.LDFlagSet> = new BehaviorSubject<LaunchDarklyClient.LDFlagSet>({});
  ldFlag$: Observable<LaunchDarklyClient.LDFlagSet> = this.ldFlagSubject.asObservable();
  // Used if LaunchDarkly is telling us what slug to use in Match Flow
  ldSlug: string = '';

  constructor(
    private configurationService: ConfigurationService<ILaunchDarklyAppConfig>,
  ) { }

  /**
   * If a client already exists, close it.
   * Connect to LaunchDarkly, but fail silently if connection fails.
   * We don't want LD issues to prevent any Match Flow functionality.
   */
  initClient(): void {
    if (this.client) { this.client.close().catch(() => {}) }

    // NOTE: there is a `Shared Local` environment on LaunchDarkly for general local development
    // if you want to use a personal LD environment, temporarily put your key
    const launchDarklyClientSdkId = this.configurationService.env.app?.launchDarklyClientSideId;

    if (launchDarklyClientSdkId) {
      this.client = LaunchDarklyClient.initialize(
        launchDarklyClientSdkId,
        {
          anonymous: true,
          custom:
          {
            userType: this.userType
          }
        },
        {
          allAttributesPrivate: true
        }
      );

      from(this.client.waitForInitialization()).pipe(
        tap(() => {
          const context = this.client?.getContext();
          this.uuid = context?.key as string;

          this.ldSlug = this.client?.variation(LDStringFeatureFlags.MATCH_FLOW_SLUG, '') as string;
        }),
        catchError(() => {
          this.fetchOrGenerateUuid();
          this.clientLoaded.next(false);
          return of(undefined);
        }),
        map(() => this.clientLoaded.next(true))
      ).subscribe();
    }
  }

  // Check if a boolean-based Feature Flag is on.
  checkFlagEnabled$(flag: LDBooleanFeatureFlags): Observable<boolean> {
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return false;
        const flagValue = this.client?.variation(flag, false);
        // We can't guarantee LaunchDarkly will send a boolean down
        if (typeof flagValue === 'boolean') {
          return flagValue;
        }
        return false;
      }));
  }

  /**
   * Get the value of a string-based Feature Flag.
   */
  checkFlagValue$(flag: LDStringFeatureFlags, defaultFlag: string = ''): Observable<string> {
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return defaultFlag;
        const flagValue = this.client?.variation(flag, defaultFlag);
        // We can't guarantee LaunchDarkly will send a string down
        if (typeof flagValue === 'string') {
          return flagValue;
        }
        return defaultFlag;
      }));
  }

  fetchOrGenerateUuid(): void {
    this.uuid = uuidv4();
  }

  async update(updatedObject: LaunchDarklyClient.LDContext): Promise<void> {
    if (!this.client) return;

    // Call client.identify to re-run LaunchDarkly with the updated user context
    await this.client.identify(updatedObject);
  }

  /**
   * Generate the LaunchDarkly-related data that should be included in raw form data for use with processors, etc.
   * Add additional flags or data here as needed.
   */
  getInitData(): ResponseUpdateItem[] {
    const uuidInitData = {
      human: 'LaunchDarkly key',
      key: 'experimentationKey',
      value: this.uuid,
    };

    return [uuidInitData];
  }

  /**
   * Used to send an event to LaunchDarkly on completion of Match Flow.
   * Generally all other analysis such as drop off should be done via Raw Form Data,
   * but if we need to expand custom metrics in Match Flow,
   * see the LaunchDarklyService implementation in Client Portal.
   * Usage of ? is probably overly cautious, but prevents errors when client has not been initialized.
   *
   * NOTE: For simplicity this is called from the ResendComponent, which loads after a successful MF submission.
   * If that final page ever changes, the location of where this is called will also need to change.
   */
  sendMatchFlowSubmitEvent(): void {
    this.client?.track('match-flow-submitted');
  }

  /**
   * Used to close the LDClient connection when the AppBootComponent is destroyed.
   */
  closeClient(): void {
    this.client?.close();
  }
}
